import type { Solution } from "./types";

// Single seam for building a Board Member's booking link, shared by the embed's result screen
// (src/presentation/result-screen.ts) and the downloadable report (src/session/report-template.ts)
// so the two never drift apart. Appends the query params the BFC WordPress theme forwards onto the
// Acuity iframe (docs/design/2026-08-27-booking-handoff-wp-template-patch.md). The WP side
// allowlists exactly these keys, so adding a key here without adding it there is inert.

/** Acuity intake field "What would you most like to accomplish in your session". Acuity's dynamic
 * link syntax is `field:<id>=value` (the DOM's `fields[field-<id>]` is the input name, not the
 * URL param). */
export const ACUITY_GOAL_FIELD_KEY = "field:19190096";

/** Acuity's own scheduling-link param for a coupon or package code, pre-applied at checkout
 * (docs/plans/2026-10-13-campaigns.md). */
export const ACUITY_COUPON_PARAM_KEY = "certificate";

// Well under the ~2,000 char URL ceiling even with a long email and name; the problem statement is
// the only unbounded value, so it is the one that gets clipped.
const MAX_PROBLEM_STATEMENT_CHARS = 500;

export interface BookingUrlContext {
  visitorFirstName?: string;
  visitorLastName?: string;
  visitorEmail?: string;
  /** The reflected problem statement, prefilled into the Acuity goal field. */
  problemStatement?: string;
  /** The session's campaign coupon code, if it started on a campaign page. */
  campaignCode?: string;
}

function clip(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}...`;
}

export function buildBookingUrl(solution: Pick<Solution, "bookingUrl">, fallbackUrl: string, context?: BookingUrlContext): string {
  // The fallback is the generic "find a board member" page, which has no Acuity form to prefill.
  if (!solution.bookingUrl) return fallbackUrl;

  const params: [string, string | undefined][] = [
    ["firstName", context?.visitorFirstName],
    ["lastName", context?.visitorLastName],
    ["email", context?.visitorEmail],
    [ACUITY_GOAL_FIELD_KEY, context?.problemStatement && clip(context.problemStatement.trim(), MAX_PROBLEM_STATEMENT_CHARS)],
    [ACUITY_COUPON_PARAM_KEY, context?.campaignCode],
  ];
  // Keys stay literal (the colon in `field:19190096` must not be escaped); only values are encoded.
  const query = params
    .filter((p): p is [string, string] => !!p[1])
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  if (!query) return solution.bookingUrl;

  const hashAt = solution.bookingUrl.indexOf("#");
  const base = hashAt === -1 ? solution.bookingUrl : solution.bookingUrl.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : solution.bookingUrl.slice(hashAt);
  return `${base}${base.includes("?") ? "&" : "?"}${query}${hash}`;
}
